import { createContext, useState, useEffect, useContext, type PropsWithChildren, Dispatch, SetStateAction } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { ItemInterface, Item as MyItem } from '../../util/Item';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from "../../util/Batch";
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { formatSupplyExpiry, parseSupplyExpiry } from "./supplyHelpers";
import { businessToday } from "../../util/businessClock";

import { addMoney } from "../../util/quantity";
export interface SupplyTask<T> extends BatchTask<T> {
	supplier: number;
	depot: number;
}

export class Item extends MyItem {
	expiry: Date | null;
	selling_price: number;
	new_barcode: string;

	constructor(data: ItemInterface & { expiry?: Date | string | null; selling_price?: number; new_barcode?: string } = {}) {
		super(data);

		this.expiry = parseSupplyExpiry(data.expiry);
		this.selling_price = data.selling_price ?? 0;
		this.new_barcode = data.new_barcode ?? '';
	}

	get uniqueId() {
		return `${super.uniqueId}-${formatSupplyExpiry(this.expiry) ?? "none"}`;
	};
}

interface ContextProps {
	pendingTasks: SupplyTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<SupplyTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: SupplyTask<Item> | undefined;
	items: any[];
	setItems: (items: any[]) => void;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

const SupplyContext = createContext<ContextProps | undefined>(undefined);

interface ProviderProps {
}

export const SupplyProvider = ({ children }: PropsWithChildren<ProviderProps>) => {
	const [pendingTasks, setPendingTasks] = useState<SupplyTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);

	const apiRef = useGridApiRef();
	const activeTask = pendingTasks?.find((task) => task.active);

	const addNewTask = () => {
		const newTask: SupplyTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			due_date: null,
			reference: Util.getSupplyNumber(),
			receiver: -1,
			depot: -1,
			supplier: -1,
			description: "",
			is_public: false,
			server_v: 0,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.items!.reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (fetchedTask) {
			const itemRecords = fetchedTask.records.map((record: any) => {
				const item = new Item(
					{
						id: record.item_id,
						item_code: record.item_code,
						item: record.item_name,
						category: record.category,
						price: record.amount,
						quantity: record.quantity,
						expiry: record.expiry,
						isFractional: record.is_fractional === true || record.IsFractional === true || items.find((c: any) => c.id === record.item_id)?.IsFractional === true,
					}
				);

				return item;
			});


			const {
				batch,
				date,
				due_date,
				depot_id,
				supplier_id,
				client_id,
				reference,
				description,
			} = fetchedTask.details;
			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: batch,
				items: itemRecords,
				date: date,
				due_date: due_date ?? null,
				depot: depot_id,
				supplier: supplier_id,
				receiver: client_id,
				reference: reference,
				description: description,
			});
		}
	}, [fetchedTask]);

	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		apiRef,
	};

	return (
		<SupplyContext.Provider value={value}>
			{children}
		</SupplyContext.Provider>
	);
};

export const useSupply = () => {
	const context = useContext(SupplyContext);

	if (!context) {
		throw new Error('useSupply must be used within a SupplyProvider');
	}
	return context;
};